import React from 'react';
import { motion } from 'framer-motion';
import {
  X, Star, Calendar, User, Clock, Target,
  ChevronRight, Download, MessageSquare, Edit,
  CheckCircle2, AlertCircle, TrendingUp, TrendingDown
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import ModalWrapper from '../../../components/ModalWrapper';

const ReviewDetails = ({ review, onClose, onEdit }) => {
  const getScoreColor = (score) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 4.0) return 'text-blue-600';
    if (score >= 3.0) return 'text-amber-600';
    return 'text-red-600';
  };

  const averageScore = Object.values(review.scores).reduce((a, b) => a + b, 0) / 
    Object.values(review.scores).length;

  const historicalData = [
    { period: '2023-Q1', score: 4.0 },
    { period: '2023-Q2', score: 4.2 },
    { period: '2023-Q3', score: 4.3 },
    { period: '2023-Q4', score: 4.5 },
    { period: review.period, score: averageScore }
  ];

  return (
    <ModalWrapper isOpen={true} onClose={onClose}>
      {/* Header */}
      <header className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Performance Review</h2>
            <p className="text-sm text-gray-500">
              {review.employeeName} • {review.period}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="space-y-8">
        {/* Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-sm text-gray-500 mb-1">Overall Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(averageScore)}`}>
              {averageScore.toFixed(1)}
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm">
              {averageScore > 4.0 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Above Average</span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-600">Needs Improvement</span>
                </>
              )}
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-sm text-gray-500 mb-1">Goals Status</div>
            <div className="text-3xl font-bold text-indigo-600">
              {review.goals.filter(g => g.status === 'Completed').length}/{review.goals.length}
            </div>
            <div className="text-sm text-gray-500 mt-2">Goals Completed</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-sm text-gray-500 mb-1">Next Review</div>
            <div className="text-3xl font-bold text-gray-900">Q2 2024</div>
            <div className="text-sm text-gray-500 mt-2">In 2 months</div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scores */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Performance Scores</h3>
            <div className="space-y-3">
              {Object.entries(review.scores).map(([metric, score]) => (
                <div key={metric}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 capitalize">
                      {metric.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className={`font-medium ${getScoreColor(score)}`}>
                      {score.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${score >= 4.5 ? 'bg-green-500' : 
                        score >= 4.0 ? 'bg-blue-500' :
                        score >= 3.0 ? 'bg-amber-500' :
                        'bg-red-500'}`}
                      style={{ width: `${(score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Radar Chart */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Radar</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={Object.entries(review.scores).map(([key, value]) => ({
                  subject: key.replace(/([A-Z])/g, ' $1').trim(),
                  score: value,
                  fullMark: 5
                }))}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <Radar
                    name="Performance"
                    dataKey="score"
                    stroke="#4F46E5"
                    fill="#4F46E5"
                    fillOpacity={0.2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Feedback */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Feedback</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="bg-green-50 rounded-xl p-4">
              <h4 className="font-medium text-green-900 mb-3">Strengths</h4>
              <ul className="space-y-2">
                {review.feedback.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2 text-green-800">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas for Improvement */}
            <div className="bg-amber-50 rounded-xl p-4">
              <h4 className="font-medium text-amber-900 mb-3">Areas for Improvement</h4>
              <ul className="space-y-2">
                {review.feedback.improvements.map((improvement, index) => (
                  <li key={index} className="flex items-start gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Goals */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Goals</h3>
          <div className="space-y-4">
            {review.goals.map((goal, index) => (
              <div key={index} className="bg-white border rounded-xl p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">{goal.title}</h4>
                    <p className="text-sm text-gray-500">{goal.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    goal.status === 'Completed' ? 'bg-green-100 text-green-700' :
                    goal.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {goal.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Due: {goal.deadline}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Historical Performance */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance History</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="period" />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#4F46E5"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 bg-gray-50">
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-white transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => onEdit(review)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Edit Review
          </button>
        </div>
      </footer>
    </ModalWrapper>
  );
};

export default ReviewDetails; 